import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { CheckCircle, AlertCircle, ArrowLeft, Key, Mail } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL, API } from '../config/apiConfig';

const ForgotPassword = ({ isOpen, onClose, onBackToLogin }) => {
  const [step, setStep] = useState('request'); // 'request', 'token', 'reset', 'success'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    token: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleForgotPasswordRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        username: formData.username,
        email: formData.email
      });
      
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
        setFormData(prev => ({ ...prev, token: response.data.reset_token }));
      }
      
      setStep('token');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to process password reset request');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (formData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        username: formData.username,
        reset_token: formData.token,
        new_password: formData.newPassword
      });
      
      setStep('success');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep('request');
    setError('');
    setResetToken('');
    setFormData({
      username: '',
      email: '',
      token: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleBackToLogin = () => {
    resetForm();
    onBackToLogin();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Key className="w-5 h-5 text-blue-600" />
            <span>Password Reset</span>
          </DialogTitle>
          <DialogDescription>
            {step === 'request' && 'Enter your username and email to reset your password'}
            {step === 'token' && 'Enter the reset token to proceed'}
            {step === 'reset' && 'Enter your new password'}
            {step === 'success' && 'Password reset completed'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step 1: Request Reset */}
          {step === 'request' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Request Password Reset</CardTitle>
                <CardDescription>
                  We'll generate a reset token for your account
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleForgotPasswordRequest} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="username">Username</Label>
                    <Input
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                      data-testid="forgot-username"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      data-testid="forgot-email"
                    />
                  </div>
                  
                  {error && (
                    <Alert className="border-red-200 bg-red-50">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <AlertDescription className="text-red-700">{error}</AlertDescription>
                    </Alert>
                  )}
                  
                  <div className="flex space-x-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleBackToLogin}
                      data-testid="back-to-login"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Login
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1"
                      data-testid="request-reset"
                    >
                      {loading ? 'Processing...' : 'Request Reset'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Enter Token */}
          {step === 'token' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Reset Token Generated</CardTitle>
                <CardDescription>
                  In production, this token would be emailed to you
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {resetToken && (
                  <Alert className="border-blue-200 bg-blue-50">
                    <Mail className="h-4 w-4 text-blue-600" />
                    <AlertDescription>
                      <div className="space-y-2">
                        <p className="text-blue-800 font-medium">Your Reset Token:</p>
                        <div className="bg-white p-2 rounded border font-mono text-sm break-all">
                          {resetToken}
                        </div>
                        <p className="text-xs text-blue-600">
                          This token expires in 1 hour. Copy it to proceed with password reset.
                        </p>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
                
                <form onSubmit={(e) => { e.preventDefault(); setStep('reset'); }} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="token">Reset Token</Label>
                    <Input
                      id="token"
                      name="token"
                      value={formData.token}
                      onChange={handleInputChange}
                      placeholder="Paste your reset token here"
                      required
                      data-testid="reset-token-input"
                    />
                  </div>
                  
                  <div className="flex space-x-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep('request')}
                      data-testid="back-to-request"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1"
                      data-testid="proceed-reset"
                    >
                      Proceed to Reset
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Reset Password */}
          {step === 'reset' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Set New Password</CardTitle>
                <CardDescription>
                  Enter your new password below
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input
                      id="newPassword"
                      name="newPassword"
                      type="password"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      required
                      minLength="6"
                      data-testid="new-password"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                      minLength="6"
                      data-testid="confirm-password"
                    />
                  </div>
                  
                  {error && (
                    <Alert className="border-red-200 bg-red-50">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <AlertDescription className="text-red-700">{error}</AlertDescription>
                    </Alert>
                  )}
                  
                  <div className="flex space-x-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep('token')}
                      data-testid="back-to-token"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      data-testid="reset-password-submit"
                    >
                      {loading ? 'Resetting...' : 'Reset Password'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Success */}
          {step === 'success' && (
            <Card>
              <CardContent className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Password Reset Successful!
                </h3>
                <p className="text-gray-600 mb-6">
                  Your password has been updated successfully. You can now log in with your new password.
                </p>
                <Button
                  onClick={handleBackToLogin}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="back-to-login-success"
                >
                  Back to Login
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPassword;